import { OverlayProvider } from '@gluestack-ui/overlay';
import { ToastProvider } from '@gluestack-ui/toast';
import type { ReactNode } from 'react';

/**
 * Two overlay hosts with the toast provider between them.
 *
 * Gluestack overlays (actionsheets, modals, popovers, and the toast list) do
 * not use a React portal. `OverlayContainer` hands its element to the nearest
 * `OverlayProvider`, which renders it as a sibling of the app tree, so an
 * overlay only sees contexts provided above that host. With a single host
 * outside `ToastProvider`, `useToast` inside any sheet read the no-op default
 * and a toast fired from a sheet silently never appeared.
 *
 * The inner host carries the app's sheets and modals, inside `ToastProvider`,
 * so their content can raise toasts. The toast list sits outside that host and
 * registers with the outer one, which renders after everything in the inner
 * one, so a toast always draws above an open sheet rather than depending on
 * which overlay happened to open first.
 */
export function OverlayHosts({ children }: { children: ReactNode }) {
  return (
    <OverlayProvider>
      <ToastProvider>
        <OverlayProvider>{children}</OverlayProvider>
      </ToastProvider>
    </OverlayProvider>
  );
}
